import {
  DeviceEnumerator,
  DevicePreferenceStore,
  PermissionManager,
  StreamAcquirer,
} from '@services/audio';

import type { ListMicrophones, MicrophonePreference } from '../types';

/**
 * One enumerator so `StreamAcquirer` can verify which device it really got,
 * and one preference store so dictation and the encounter recorder remember
 * the same microphone (`audioDevicePreference` in localStorage).
 */
export const enumerator = new DeviceEnumerator();
export const preferenceStore = new DevicePreferenceStore();
export const permissionManager = new PermissionManager();
export const streamAcquirer = new StreamAcquirer(enumerator);

/** RecordingContext also mirrors the choice under this legacy key. */
const LEGACY_SELECTED_KEY = 'selectedMicrophoneId';

export const listMicrophones: ListMicrophones = async ({ interactive }) => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return [];
  const permission = interactive
    ? await permissionManager.requestPermission()
    : await permissionManager.checkPermission();
  if (!permission.granted) return [];
  return enumerator.enumerate();
};

export const microphonePreference: MicrophonePreference = {
  load: devices => {
    const stored = preferenceStore.load();
    return stored ? preferenceStore.findBestMatch(stored, devices) : null;
  },
  save: device => {
    preferenceStore.save(device);
    try {
      window.localStorage.setItem(LEGACY_SELECTED_KEY, device.browserDeviceId);
    } catch {
      // Storage blocked: the bundle above may have failed too; nothing else to do.
    }
  },
};

/**
 * Resolves the microphone to open when the caller did not name one: the
 * remembered device if it is still plugged in, otherwise the browser default.
 */
export async function rememberedDeviceId(): Promise<string | undefined> {
  const devices = enumerator.getCachedDevices().length
    ? enumerator.getCachedDevices()
    : await enumerator.enumerate();
  return microphonePreference.load(devices)?.browserDeviceId;
}
